import { useCallback, useEffect, useRef, useState } from "react";
import type { CueScript } from "../types";
import { normalizeScript } from "./imports";
export interface ScriptVersion {
  id: string;
  script: CueScript;
  savedAt: number;
  label: string;
}
export interface HistoryController {
  versions: ScriptVersion[];
  storageError: string | null;
  save: (script: CueScript, label?: string) => void;
  remove: (id: string) => void;
  replace: (versions: ScriptVersion[]) => void;
}
const KEY = "cue-studio-versions-v1",
  LIMIT = 2 * 1024 * 1024;
function prependVersion(items: ScriptVersion[], script: CueScript, label: string): ScriptVersion[] {
  if (items[0]?.script.id === script.id && items[0].script.html === script.html &&
    items[0].script.notes === script.notes && items[0].script.title === script.title) return items;
  const next = [{ id: crypto.randomUUID(), script: { ...script, tags: [...script.tags] }, savedAt: Date.now(), label: label.slice(0, 120) }, ...items];
  let size = 0;
  return next.filter(item => { size += JSON.stringify(item).length; return size < LIMIT; }).slice(0, 100);
}
export function prepareRestoredVersions(items: ScriptVersion[], values: unknown, idMap: ReadonlyMap<string, string>): ScriptVersion[] {
  if (!Array.isArray(values)) return items;
  let next = items;
  for (const version of values.slice(0, 100)) {
    const script = normalizeScript(version?.script);
    if (!script) continue;
    script.id = idMap.get(script.id) || script.id;
    next = prependVersion(next, script, typeof version.label === "string" && version.label ? version.label : "Restored version");
  }
  return next;
}
function read(): ScriptVersion[] {
  try {
    const items = JSON.parse(localStorage.getItem(KEY) || "[]");
    if (!Array.isArray(items)) return [];
    return items.slice(0, 100).flatMap((x) => {
      const script = normalizeScript(x?.script);
      return script &&
        typeof x.id === "string" &&
        typeof x.savedAt === "number" &&
        Number.isFinite(x.savedAt) &&
        x.savedAt >= 0 &&
        x.savedAt <= 8.64e15
        ? [
            {
              id: x.id,
              script,
              savedAt: x.savedAt,
              label:
                typeof x.label === "string" && x.label
                  ? x.label.slice(0, 120)
                  : "Saved version",
            },
          ]
        : [];
    });
  } catch {
    return [];
  }
}
export function useHistory(
  onError: (message: string) => void,
  enabled = true,
): HistoryController {
  const [versions, setVersions] = useState(read);
  const [storageError, setStorageError] = useState<string | null>(null);
  const failureNotified = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let serialized: string | undefined;
    const persist = () => {
      try {
        localStorage.setItem(KEY, serialized ??= JSON.stringify(versions));
        setStorageError(null);
        failureNotified.current = false;
      } catch {
        const message =
          "Version storage is full. Your live script remains in the editor; export a backup.";
        setStorageError(message);
        if (!failureNotified.current) {
          failureNotified.current = true;
          onError(message);
        }
        // A recovered quota must save the pending version even if the operator
        // only changes a title, pauses, or makes no further edits.
        retry = setTimeout(persist, 2000);
      }
    };
    persist();
    return () => clearTimeout(retry);
  }, [versions, onError, enabled]);
  const save = useCallback((script: CueScript, label = "Saved version") => {
    setVersions((items) => prependVersion(items, script, label));
  }, []);
  const remove = (id: string) =>
    setVersions((items) => items.filter((x) => x.id !== id));
  return { versions, storageError, save, remove, replace: setVersions };
}
